import { Box, BoxProps, Text, TextProps } from '@everlywell/ui-kit';
import ApplePayWidget from 'components/ApplePayWidget';
import React from 'react';
import { Order } from 'utils/types';

import { getFormattedSummary } from './CartSummary.utils';

export type Props = Omit<BoxProps, 'order'> & {
  order: Order;
};

const Label: React.FC<TextProps> = ({ ...props }) => (
  <Text as="span" fontSize="md" color="tints.darkGrey" {...props} />
);

const Value: React.FC<TextProps> = ({ ...props }) => (
  <Text as="span" fontSize="md" fontWeight="medium" {...props} />
);

/**
 * Displays the order details
 */

export type DetailsProps = { order: Order } & Omit<BoxProps, 'order'>;

const Details: React.FC<DetailsProps> = ({
  order,
  ...boxProps
}: Props): JSX.Element | null => {
  if (!order) {
    return null;
  }

  const summary = getFormattedSummary(order);

  return (
    <Box as="ul" display="flex" flexDirection="column" gap={2} {...boxProps}>
      <Box as="li" display="flex" justifyContent="space-between">
        <Label color="tints.darkGrey">Subtotal</Label>
        <Value>{summary.subtotal.formatted}</Value>
      </Box>

      <Box as="li" display="flex" justifyContent="space-between">
        <Label color="tints.darkGrey">Shipping</Label>
        <Value color="utility.success">
          {summary.shipping.raw === '0.00'
            ? 'Free'
            : summary.shipping.formatted}
        </Value>
      </Box>

      {summary.discount.raw !== '0.00' && (
        <Box as="li" display="flex" justifyContent="space-between">
          <Label color="tints.darkGrey">Discount</Label>
          <Value color="utility.sale">{summary.discount.formatted}</Value>
        </Box>
      )}

      <Box as="li" display="flex" justifyContent="space-between">
        <Label color="tints.darkGrey">Tax</Label>
        <Value>Calculated at checkout</Value>
      </Box>
    </Box>
  );
};

/**
 * Displays the order's total
 */
const Total: React.FC<{ order: Order }> = ({ order }) => {
  const summary = getFormattedSummary(order);
  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box display="flex" flexDirection="column">
        <Text fontSize="lg" fontWeight="medium">
          Total
        </Text>
        <ApplePayWidget amount={order.amount ?? '0'} />
      </Box>
      <Text fontSize="x-large" fontWeight="medium">
        {summary.total.formatted}
      </Text>
    </Box>
  );
};

const CartSummary = {
  Details,
  Total,
};

export default CartSummary;
